"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo } from "react";
import { useFormatter, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary, usePartialPaymentOrders } from "@/features/dashboard/hooks";
import {
  PartialPaymentBanner,
  PartialPaymentBannerSkeleton,
} from "@/features/dashboard/components/PartialPaymentBanner";
import {
  findPartialOrderById,
  findPartialOrderForCourse,
} from "@/features/dashboard/partial-payment";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type CoursePrice,
  type Course,
} from "../api";
import type { Locale } from "@/lib/i18n";
import { useCoursePricing } from "./CoursePricingProvider";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { CoursePricingOwnedStatusBanner } from "./CoursePricingOwnedStatusBanner";
import { CohortWaitlistForm } from "./CohortWaitlistForm";
import { CourseCohortLine } from "./CourseCohortLine";
import { SprintCountdown } from "./SprintCountdown";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  formatTierUpgradeCtaLabel,
  isPlanIdActiveUpgradeTarget,
  isSidebarPlanRowOwnedTier,
  normalizePricingPlanId,
  resolvePlanPricingUiKind,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import {
  getSeatIndicator,
  hasFutureSprint,
} from "../lib/course-pricing-helpers";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { scrollToCoursePricingComparison } from "../lib/scroll-course-pricing";
import { signInHref } from "@/lib/post-login-redirect";

// ── Inline price display (mirrors single-plan CTA) ───────────────────────────

function PlanPriceDisplay({ price }: { price: CoursePrice }) {
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const hasSale =
    !price.isFree &&
    sale !== undefined &&
    Number.isFinite(sale) &&
    sale < list;
  const currency = price.currency === "BDT" ? "৳" : "$";

  if (price.isFree) {
    return (
      <span className="text-[32px] font-bold text-emerald-600">Free</span>
    );
  }

  return (
    <div className="flex items-baseline gap-2.5">
      {hasSale && (
        <span className="text-[32px] font-bold text-neutral-950">
          {currency}
          {formatCourseMoneyEnIn(sale)}
        </span>
      )}
      <span
        className={cn(
          hasSale
            ? "text-[18px] text-neutral-400 line-through"
            : "text-[32px] font-bold text-neutral-950",
        )}
      >
        {currency}
        {formatCourseMoneyEnIn(list)}
      </span>
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────────────────

function PricingSidebarLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="aspect-video w-full rounded-lg" />
      <Skeleton className="h-4 w-32 rounded-md" />
      <Skeleton className="h-8 w-40 rounded-md" />
      <Skeleton className="h-[52px] w-full rounded-[10px]" />
    </div>
  );
}

// ── Legacy-full banner ───────────────────────────────────────────────────────

// ── Main export ───────────────────────────────────────────────────────────────

type Props = {
  courseId: string;
  slug: string;
  fallbackPrice: CoursePrice;
  eligibility?: Course["eligibility"];
  locale: Locale;
  firstLessonId?: string | null;
  className?: string;
  /** Course thumbnail for multi-tier compact sidebar only */
  thumbnailUrl?: string | null;
  /** The course's earliest cohort, used only for a course sold without plans; with plans, each plan's own date speaks. */
  nextCohortStartAt?: string | null;
};

export function CoursePricingBlock({
  courseId,
  slug,
  fallbackPrice,
  eligibility,
  locale,
  firstLessonId,
  className,
  thumbnailUrl,
  nextCohortStartAt,
}: Props) {
  const t = useTranslations("courseDetail");
  const seatLabels = {
    full: t("seats.full"),
    limited: t("seats.limited"),
    onlyLeft: (count: number) => t("seats.onlyLeft", { count }),
  };
  const format = useFormatter();
  const {
    plans,
    selectedPlan,
    recommendedPlan,
    selectedPlanId,
    setSelectedPlanId,
    mode,
  } = useCoursePricing();
  const { isAuthenticated, getToken } = useAuth();
  const token = getToken();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityPassed =
    hasEligibilityGate && isAuthenticated && eligibilityStatus?.passed === true;
  const eligibilityLocked = hasEligibilityGate && !eligibilityPassed;
  const eligibilityQuizSlug =
    eligibilityStatus?.quiz?.slug ?? eligibility?.quizSlug ?? "";
  const eligibilityAttemptHref = `/${locale}/quiz-exams/${eligibilityQuizSlug}/attempt`;
  const eligibilityCtaHref = isAuthenticated
    ? eligibilityAttemptHref
    : signInHref(locale, eligibilityAttemptHref);
  const eligibilityLockedReason = t("eligibilityTierLocked");
  const lastAttempt = eligibilityStatus?.lastAttempt;
  const { data: library } = useLibrary(token ?? undefined);
  const { data: partialOrders, isLoading: partialOrdersLoading } =
    usePartialPaymentOrders(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const partialOrder =
    findPartialOrderById(partialOrders, ownedCourse?.orderId) ??
    findPartialOrderForCourse(partialOrders, courseId);
  const isOwned = !!ownedCourse;
  /** Must run before any early `return` — Rules of Hooks (multi branch uses this). */
  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.sortOrder - b.sortOrder),
    [plans],
  );

  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  useEffect(() => {
    if (mode !== "multi") return;
    if (!isAuthenticated || !isOwned || pricingUiModel.isLegacyEnrollmentWithoutPlanId)
      return;
    const id = ownedCourse?.pricingPlanId;
    if (!id) return;
    if (
      !sortedPlans.some(
        (p) => normalizePricingPlanId(p._id) === normalizePricingPlanId(id),
      )
    ) {
      return;
    }
    setSelectedPlanId((prev) => (prev === null ? id : prev));
  }, [
    mode,
    isAuthenticated,
    isOwned,
    pricingUiModel.isLegacyEnrollmentWithoutPlanId,
    ownedCourse?.pricingPlanId,
    setSelectedPlanId,
    sortedPlans,
  ]);

  const eligibilityNotice = hasEligibilityGate ? (
    <div className="mb-4 space-y-3 rounded-xl border border-orange-100 bg-orange-50 p-4">
      {eligibilityPassed ? (
        <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
          {t("eligibilityVerified")}
        </span>
      ) : (
        <>
          {!isAuthenticated && (
            <p className="text-sm text-orange-800">{t("eligibilityLoginHint")}</p>
          )}
          {lastAttempt && (
            <p className="text-sm text-orange-800">
              {t("eligibilityLastAttempt", {
                percentage: Math.round(lastAttempt.percentage),
                date: format.dateTime(new Date(lastAttempt.completedAt), {
                  dateStyle: "medium",
                }),
              })}
            </p>
          )}
          <Button asChild className="w-full rounded-[10px] font-semibold">
            <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
          </Button>
        </>
      )}
    </div>
  ) : null;

  if (mode === "loading") {
    return (
      <div className={cn("scroll-mt-28 space-y-3", className)} id="course-pricing-sidebar">
        <PricingSidebarLoadingSkeleton />
      </div>
    );
  }

  if (mode === "legacy") {
    return (
      <div className={cn("scroll-mt-28", className)} id="course-pricing-sidebar">
        {eligibilityNotice}
        {partialOrdersLoading && <PartialPaymentBannerSkeleton className="mb-4" />}
        {partialOrder && (
          <PartialPaymentBanner order={partialOrder} locale={locale} className="mb-4" />
        )}
        <CourseCohortLine iso={nextCohortStartAt} className="mb-4" />
        <div className="mb-5">
          <PlanPriceDisplay price={fallbackPrice} />
        </div>
        <CourseEnrollCta
          courseId={courseId}
          slug={slug}
          price={fallbackPrice}
          locale={locale}
          firstLessonId={firstLessonId}
          lockedReason={eligibilityLocked ? eligibilityLockedReason : undefined}
          lockedLabel={eligibilityLocked ? t("eligibilityTierLocked") : undefined}
          className="mb-4"
        />
      </div>
    );
  }

  if (mode === "single" && selectedPlan) {
    const singleSeatIndicator = getSeatIndicator(selectedPlan, seatLabels);
    const singleSeatFullForBuyer =
      singleSeatIndicator?.full === true && !isOwned;
    return (
      <div className={cn("scroll-mt-28", className)} id="course-pricing-sidebar">
        {eligibilityNotice}
        {partialOrdersLoading && <PartialPaymentBannerSkeleton className="mb-4" />}
        {partialOrder && (
          <PartialPaymentBanner order={partialOrder} locale={locale} className="mb-4" />
        )}
        {isAuthenticated && isOwned ? (
          <CoursePricingOwnedStatusBanner
            locale={locale}
            className="mb-4"
            planDisplayName={
              (pricingUiModel.effectiveOwnedPlanForCopy &&
                (getLocalizedField(
                  pricingUiModel.effectiveOwnedPlanForCopy.name_en,
                  pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
                  locale,
                ) ??
                  pricingUiModel.effectiveOwnedPlanForCopy.name_en)) ||
              (getLocalizedField(selectedPlan.name_en, selectedPlan.name_bn, locale) ??
                selectedPlan.name_en)
            }
            hasActiveUpgradeTarget={pricingUiModel.hasActiveUpgradeTarget}
          />
        ) : null}
        <CourseCohortLine iso={selectedPlan.cohortStartAt} className="mb-4" />
        <div className="mb-5">
          <PlanPriceDisplay price={selectedPlan.price} />
        </div>
        {singleSeatIndicator && (
          <p
            className={cn(
              "mb-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold",
              singleSeatIndicator.warm
                ? "bg-orange-100 text-orange-800"
                : "bg-neutral-100 text-neutral-700",
            )}
          >
            {singleSeatIndicator.label}
          </p>
        )}
        {hasFutureSprint(selectedPlan.sprintEndsAt) && (
          <SprintCountdown
            endsAt={selectedPlan.sprintEndsAt!}
            label={t("sprintCountdown.label")}
            size="compact"
            className="mb-4"
          />
        )}
        <div>
          {singleSeatFullForBuyer ? (
            <CohortWaitlistForm
              courseSlug={slug}
              planSlug={selectedPlan.slug ?? selectedPlan._id}
              className="mb-4"
            />
          ) : (
            <CourseEnrollCta
              courseId={courseId}
              slug={slug}
              price={selectedPlan.price}
              pricingPlanId={selectedPlan._id}
              locale={locale}
              firstLessonId={firstLessonId}
              className="mb-4"
              lockedReason={eligibilityLocked ? eligibilityLockedReason : undefined}
              lockedLabel={eligibilityLocked ? t("eligibilityTierLocked") : undefined}
            />
          )}
        </div>
      </div>
    );
  }

  const selectedIsUpgradeTarget =
    !!selectedPlan && isPlanIdActiveUpgradeTarget(selectedPlan._id, pricingUiModel);

  const selectedKind =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePlanPricingUiKind(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const upgradePathBlocked =
    !!isAuthenticated &&
    !!isOwned &&
    !!selectedPlan &&
    selectedKind === "LOWER_OR_SIDEWAYS";

  const upgradeUnavailableReason = upgradePathBlocked
    ? "This tier is not offered as an upgrade from your current access. You can continue learning on your included plan or pick an upgrade tier listed for your level."
    : undefined;
  const copy = getOwnershipCopy(locale);
  const selectedName =
    selectedPlan &&
    (getLocalizedField(selectedPlan.name_en, selectedPlan.name_bn, locale) ??
      selectedPlan.name_en);
  const upgradeLabel =
    selectedPlan && selectedIsUpgradeTarget
      ? formatTierUpgradeCtaLabel(selectedPlan, pricingUiModel, {
          upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
          upgradeFree: () => t("planOwnership.upgradeFree"),
        })
      : undefined;
  const upgradeUnavailableLabel =
    selectedPlan && upgradePathBlocked
      ? `${copy.upgradePrefix} ${selectedName}`
      : undefined;
  const selectedPlanOwnedState =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePricingUiOwnedState(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const nominalLegacyName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const ownedTierChipName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  const sidebarEnrolledPeerNotice =
    selectedPlan && selectedPlanOwnedState
      ? selectedPlanOwnedState.kind === "owned-other" && !selectedPlanOwnedState.upgradeable
        ? t("planOwnership.coveredByPlan", {
            planName: ownedTierChipName ?? t("planOwnership.yourPlanShort"),
          })
        : selectedPlanOwnedState.kind === "owned-legacy-peer" && nominalLegacyName
          ? t("planOwnership.enrolledInPlanChip", { planName: nominalLegacyName })
          : undefined
      : undefined;

  const upgradeUnavailableReasonEffective = sidebarEnrolledPeerNotice
    ? undefined
    : upgradeUnavailableReason;
  const upgradeUnavailableLabelEffective = sidebarEnrolledPeerNotice
    ? undefined
    : upgradeUnavailableLabel;

  const compactSeatIndicator = selectedPlan ? getSeatIndicator(selectedPlan, seatLabels) : null;
  const compactSeatFullForBuyer = compactSeatIndicator?.full === true && !isOwned;

  function compactAmount(price: CoursePrice): string {
    const list = coalesceCourseMoney(price.list);
    const sale = price.sale;
    const amount =
      !price.isFree &&
      sale !== undefined &&
      Number.isFinite(sale) &&
      sale < list
        ? sale
        : list;
    const sym = price.currency === "BDT" ? "৳" : "$";
    if (price.isFree) return t("sidebarPlanPriceFree");
    return `${sym}${formatCourseMoneyEnIn(amount)}`;
  }

  return (
    <div className={cn("scroll-mt-28", className)} id="course-pricing-sidebar">
      {eligibilityNotice}
      {partialOrdersLoading && <PartialPaymentBannerSkeleton className="mb-4" />}
      {partialOrder && (
        <PartialPaymentBanner order={partialOrder} locale={locale} className="mb-4" />
      )}
      {isAuthenticated && isOwned ? (
        <CoursePricingOwnedStatusBanner
          locale={locale}
          className="mb-4"
          planDisplayName={
            (pricingUiModel.effectiveOwnedPlanForCopy &&
              (getLocalizedField(
                pricingUiModel.effectiveOwnedPlanForCopy.name_en,
                pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
                locale,
              ) ?? pricingUiModel.effectiveOwnedPlanForCopy.name_en)) ||
            t("planOwnership.yourPlanShort")
          }
          hasActiveUpgradeTarget={pricingUiModel.hasActiveUpgradeTarget}
        />
      ) : null}

      {thumbnailUrl ? (
        <div className="relative mb-4 aspect-video w-full overflow-hidden rounded-lg bg-neutral-100">
          <Image
            src={thumbnailUrl}
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 360px"
          />
        </div>
      ) : null}

      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
        {t("sidebarPlanPickerEyebrow")}
      </p>
      <div className="mb-4 space-y-2" role="radiogroup" aria-label={t("sidebarPlanPickerAria")}>
        {sortedPlans.map((plan) => {
          const pName =
            getLocalizedField(plan.name_en, plan.name_bn, locale) ?? plan.name_en;
          const isRowSelected = selectedPlanId === plan._id;
          const isRec = recommendedPlan?._id === plan._id;
          const isOwnedTierRow =
            isAuthenticated && isOwned && isSidebarPlanRowOwnedTier(plan, pricingUiModel);
          return (
            <Button
              key={plan._id}
              type="button"
              role="radio"
              variant="choice"
              size="option"
              aria-checked={isRowSelected}
              onClick={() => {
                setSelectedPlanId(plan._id);
              }}
              className={cn(
                !isRowSelected && isOwnedTierRow && "border-emerald-200 bg-emerald-50/50",
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-neutral-900">
                  {pName}
                </span>
                {isRec && (
                  <span className="mt-0.5 block text-[11px] font-medium text-primary">
                    {t("sidebarRecommendedShort")}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-[13px] font-bold tabular-nums text-neutral-900">
                {compactAmount(plan.price)}
              </span>
            </Button>
          );
        })}
      </div>

      {selectedPlan ? (
        <div className="space-y-3">
          <CourseCohortLine iso={selectedPlan.cohortStartAt} />

          {hasFutureSprint(selectedPlan.sprintEndsAt) && (
            <SprintCountdown
              endsAt={selectedPlan.sprintEndsAt!}
              label={t("sprintCountdown.label")}
              size="compact"
            />
          )}

          {eligibilityLocked ? (
            <Button className="w-full rounded-[10px] font-semibold" asChild>
              <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
            </Button>
          ) : compactSeatFullForBuyer ? (
            <CohortWaitlistForm
              courseSlug={slug}
              planSlug={selectedPlan.slug ?? selectedPlan._id}
              className="w-full"
            />
          ) : (
            <CourseEnrollCta
              courseId={courseId}
              slug={slug}
              price={selectedPlan.price}
              pricingPlanId={selectedPlan._id}
              enrollLabel={t("enrollWithPrice", { price: compactAmount(selectedPlan.price) })}
              upgradeToPlanId={selectedIsUpgradeTarget ? selectedPlan._id : undefined}
              upgradeLabel={upgradeLabel}
              upgradeUnavailableReason={upgradeUnavailableReasonEffective}
              upgradeUnavailableLabel={upgradeUnavailableLabelEffective}
              enrolledPeerNotice={sidebarEnrolledPeerNotice}
              lockedReason={eligibilityLocked ? eligibilityLockedReason : undefined}
              lockedLabel={eligibilityLocked ? t("eligibilityTierLocked") : undefined}
              locale={locale}
              firstLessonId={firstLessonId}
              className="w-full"
            />
          )}

          <Link
            href="#course-pricing-comparison"
            onClick={(e) => {
              e.preventDefault();
              scrollToCoursePricingComparison();
            }}
            className="inline-flex text-[13px] font-semibold text-primary-ink underline-offset-4 hover:underline"
          >
            {t("comparePlansLink")}
          </Link>
        </div>
      ) : null}
    </div>
  );
}
